import React from 'react';
import { Box, Typography, Paper, useTheme } from '@mui/material';

interface TimeSeriesChartProps {
  title: string;
  data: Array<{ date: string; value: number; label?: string }>;
  height?: number;
  color?: string;
}

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({ 
  title, 
  data, 
  height = 200,
  color = 'primary.main'
}) => {
  const theme = useTheme();
  
  const getChartColor = (colorString: string): string => {
    // Handle predefined color keys
    switch (colorString) {
      case 'primary.main':
        return theme.palette.primary.main;
      case 'secondary.main':
        return theme.palette.secondary.main;
      case 'success.main':
        return theme.palette.success.main;
      case 'error.main':
        return theme.palette.error.main;
      case 'warning.main':
        return theme.palette.warning.main;
      case 'info.main':
        return theme.palette.info.main;
      default:
        return colorString; // Return as-is for direct color values
    }
  };

  const actualColor = getChartColor(color);
  
  if (data.length === 0) {
    return (
      <Paper sx={{ p: 2, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value), 1);
  const minValue = Math.min(...data.map(d => d.value), 0);
  const range = maxValue - minValue || 1;

  // SVG dimensions (viewBox-based for responsiveness)
  const svgWidth = 500;
  const chartHeight = height - 60;
  const yAxisWidth = 40; // Space for y-axis labels
  const paddingTop = 15;
  const paddingBottom = 25; // Space for x-axis labels
  const paddingRight = 15;
  const chartWidth = svgWidth - yAxisWidth - paddingRight;
  const chartInnerHeight = chartHeight - paddingTop - paddingBottom;

  // Y-axis tick values (5 ticks)
  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const val = minValue + ((4 - i) / 4) * range;
    return { value: Math.round(val), y: paddingTop + (i / 4) * chartInnerHeight };
  });

  // Generate points for the line
  const points = data.map((point, index) => {
    const x = yAxisWidth + (index / Math.max(data.length - 1, 1)) * chartWidth;
    const y = paddingTop + chartInnerHeight - ((point.value - minValue) / range) * chartInnerHeight;
    return `${x},${y}`;
  }).join(' ');

  // Determine which x-axis labels to show (skip some if too many)
  const maxLabels = 7;
  const labelInterval = data.length > maxLabels ? Math.ceil(data.length / maxLabels) : 1;

  return (
    <Paper sx={{ p: 2, height }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ width: '100%', height: chartHeight }}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${svgWidth} ${chartHeight}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Y-axis labels and grid lines */}
          {yTicks.map((tick, i) => (
            <g key={`ytick-${i}`}>
              <text
                x={yAxisWidth - 6}
                y={tick.y + 4}
                textAnchor="end"
                fontSize="10"
                fill={theme.palette.text.secondary}
              >
                {tick.value}
              </text>
              <line
                x1={yAxisWidth}
                y1={tick.y}
                x2={svgWidth - paddingRight}
                y2={tick.y}
                stroke={theme.palette.divider}
                strokeWidth={1}
              />
            </g>
          ))}
          
          {/* Data line */}
          <polyline
            points={points}
            fill="none"
            stroke={actualColor}
            strokeWidth={2}
          />
          
          {/* Data points with value labels */}
          {data.map((point, index) => {
            const x = yAxisWidth + (index / Math.max(data.length - 1, 1)) * chartWidth;
            const y = paddingTop + chartInnerHeight - ((point.value - minValue) / range) * chartInnerHeight;
            
            return (
              <g key={index}>
                <circle
                  cx={x}
                  cy={y}
                  r={4}
                  fill={actualColor}
                />
                {/* Show value label on each point */}
                <text
                  x={x}
                  y={y - 8}
                  textAnchor="middle"
                  fontSize="9"
                  fill={theme.palette.text.primary}
                  fontWeight="bold"
                >
                  {point.value > 0 ? point.value : ''}
                </text>
              </g>
            );
          })}

          {/* X-axis labels (inside SVG so they align with data points) */}
          {data.map((point, index) => {
            if (index % labelInterval !== 0 && index !== data.length - 1) return null;
            const x = yAxisWidth + (index / Math.max(data.length - 1, 1)) * chartWidth;
            return (
              <text
                key={`xlabel-${index}`}
                x={x}
                y={chartHeight - 4}
                textAnchor="middle"
                fontSize="9"
                fill={theme.palette.text.secondary}
              >
                {new Date(point.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </text>
            );
          })}
        </svg>
      </Box>
    </Paper>
  );
};

export default TimeSeriesChart;
